import {
  type ApiEnvelope,
  isApiSuccessCode,
  parseApiEnvelopeJson,
  readRetryAfterSecondsFromHeaders,
  resolveApiHttpFailure,
} from '@glyphic/shared-core'

import { ApiError } from './errors'

export type RequestInterceptorContext = {
  path: string
  url: string
  init: RequestInit
}

export type RequestInterceptor = (
  ctx: RequestInterceptorContext,
) => void | Promise<void>

export type ResponseInterceptorContext = {
  path: string
  url: string
  init: RequestInit
  response: Response
  rawBody: string
  parsed: ApiEnvelope<unknown> | null
}

export type ResponseInterceptor = (
  ctx: ResponseInterceptorContext,
) => void | Promise<void>

export type ErrorInterceptorContext = {
  path: string
  url: string
  init: RequestInit
  error: unknown
}

export type ErrorInterceptor = (
  ctx: ErrorInterceptorContext,
) => void | Promise<void>

export type HttpClientOptions = {
  getBaseUrl: () => string
}

export class HttpClient {
  private readonly requestInterceptors: RequestInterceptor[] = []
  private readonly responseInterceptors: ResponseInterceptor[] = []
  private readonly errorInterceptors: ErrorInterceptor[] = []

  constructor(private readonly options: HttpClientOptions) {}

  useRequest(interceptor: RequestInterceptor): this {
    this.requestInterceptors.push(interceptor)
    return this
  }

  useResponse(interceptor: ResponseInterceptor): this {
    this.responseInterceptors.push(interceptor)
    return this
  }

  useError(interceptor: ErrorInterceptor): this {
    this.errorInterceptors.push(interceptor)
    return this
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const base = this.options.getBaseUrl().replace(/\/+$/, '')
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const url = `${base}${normalizedPath}`

    const headers = new Headers(init.headers)
    if (
      init.body != null &&
      !headers.has('Content-Type') &&
      !(init.body instanceof FormData)
    ) {
      headers.set('Content-Type', 'application/json')
    }

    const ctx: RequestInterceptorContext = {
      path: normalizedPath,
      url,
      init: { ...init, headers },
    }

    for (const fn of this.requestInterceptors) {
      await fn(ctx)
    }

    let response: Response
    try {
      response = await fetch(ctx.url, ctx.init)
    } catch (err) {
      await this.notifyError(ctx, err)
      throw err
    }

    const rawBody = await response.text()
    const parsed = parseApiEnvelopeJson(rawBody)

    const respCtx: ResponseInterceptorContext = {
      path: ctx.path,
      url: ctx.url,
      init: ctx.init,
      response,
      rawBody,
      parsed,
    }

    for (const fn of this.responseInterceptors) {
      await fn(respCtx)
    }

    if (!response.ok || parsed == null || !isApiSuccessCode(parsed.code)) {
      const failure = resolveApiHttpFailure({
        status: response.status,
        rawBody,
        parsed,
        retryAfterSeconds: readRetryAfterSecondsFromHeaders(response.headers),
        rateLimitFallback: '请求过于频繁',
      })
      const err = new ApiError(
        failure.message,
        failure.code,
        failure.status,
        failure.detail,
        failure.retryAfterSeconds,
      )
      await this.notifyError(ctx, err)
      throw err
    }

    return parsed.data as T
  }

  private async notifyError(
    ctx: RequestInterceptorContext,
    error: unknown,
  ): Promise<void> {
    const payload: ErrorInterceptorContext = {
      path: ctx.path,
      url: ctx.url,
      init: ctx.init,
      error,
    }
    for (const fn of this.errorInterceptors) {
      await fn(payload)
    }
  }
}
